import { useState, useCallback, useRef, useEffect } from "react";

interface VoiceSyncOptions {
  content: string;
  lineHeight: number;
  onScrollTo: (position: number) => void;
}

interface VoiceSyncState {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  confidence: number;
  currentWordIndex: number;
  error: string | null;
}

// Normalize text for comparison
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, "") // Remove punctuation
    .replace(/\s+/g, " ")
    .trim();
}

// Find best matching position in content
function findBestMatch(
  spokenWords: string[],
  contentWords: string[],
  currentIndex: number,
  searchWindow: number = 50
): { index: number; confidence: number } {
  if (spokenWords.length === 0) {
    return { index: currentIndex, confidence: 0 };
  }

  const startIndex = Math.max(0, currentIndex - 10);
  const endIndex = Math.min(contentWords.length, currentIndex + searchWindow);

  let bestMatchIndex = currentIndex;
  let bestMatchScore = 0;

  // Use sliding window to find best match
  for (let i = startIndex; i < endIndex; i++) {
    let matchScore = 0;
    let matchCount = 0;

    for (let j = 0; j < spokenWords.length && i + j < contentWords.length; j++) {
      if (normalizeText(spokenWords[j]) === normalizeText(contentWords[i + j])) {
        matchScore += 1;
        matchCount++;
      }
    }

    // Weight matches that are closer to current position slightly
    const proximityBonus = 1 - Math.abs(i - currentIndex) / searchWindow * 0.1;
    const adjustedScore = matchScore * proximityBonus;

    if (adjustedScore > bestMatchScore) {
      bestMatchScore = adjustedScore;
      bestMatchIndex = i + Math.floor(spokenWords.length / 2);
    }
  }

  const confidence = spokenWords.length > 0 ? bestMatchScore / spokenWords.length : 0;
  return { index: bestMatchIndex, confidence };
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  [index: number]: {
    readonly transcript: string;
    readonly confidence: number;
  };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent {
  readonly error: string;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;


interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

export function useVoiceSync({ content, lineHeight, onScrollTo }: VoiceSyncOptions) {
  const [state, setState] = useState<VoiceSyncState>({
    isListening: false,
    isSupported: typeof window !== "undefined" && "webkitSpeechRecognition" in window,
    transcript: "",
    confidence: 0,
    currentWordIndex: 0,
    error: null,
  });

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const contentWordsRef = useRef<string[]>([]);
  const currentWordIndexRef = useRef(0);
  const isListeningRef = useRef(false);

  // Parse content into words
  useEffect(() => {
    contentWordsRef.current = content.split(/\s+/).filter(Boolean);
  }, [content]);

  // Calculate scroll position from word index
  const calculateScrollPosition = useCallback((wordIndex: number): number => {
    const lines = content.split("\n");
    let wordCount = 0;
    let lineIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const lineWords = lines[i].split(/\s+/).filter(Boolean).length;
      if (wordCount + lineWords >= wordIndex) {
        lineIndex = i;
        break;
      }
      wordCount += lineWords;
    }

    return lineIndex * lineHeight;
  }, [content, lineHeight]);

  const startListening = useCallback(() => {
    if (!state.isSupported) {
      setState((prev) => ({ ...prev, error: "Speech recognition not supported" }));
      return;
    }

    try {
      // Create recognition instance
      const recognitionFactory = window as Window & {
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
        SpeechRecognition?: SpeechRecognitionConstructor;
      };
      const SpeechRecognition = recognitionFactory.webkitSpeechRecognition || recognitionFactory.SpeechRecognition;
      if (!SpeechRecognition) {
        throw new Error("Speech recognition not available");
      }
      const recognition = new SpeechRecognition() as SpeechRecognitionLike;

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = navigator.language || "en-US";
      recognition.maxAlternatives = 3;

      recognition.onstart = () => {
        isListeningRef.current = true;
        setState((prev) => ({ 
          ...prev, 
          isListening: true, 
          error: null 
        }));
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const results = event.results;
        let interimTranscript = "";
        let finalTranscript = "";

        for (let i = event.resultIndex; i < results.length; i++) {
          const result = results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        const currentTranscript = finalTranscript || interimTranscript;
        if (!currentTranscript) return;

        const spokenWords = currentTranscript.split(/\s+/).filter(Boolean);
        
        // Find matching position
        const { index, confidence } = findBestMatch(
          spokenWords.slice(-5), // Use last 5 spoken words
          contentWordsRef.current,
          currentWordIndexRef.current
        );

        if (confidence > 0.4) { // Only scroll if confidence is high enough
          currentWordIndexRef.current = index;
          const scrollPosition = calculateScrollPosition(index);
          onScrollTo(scrollPosition);
        }

        setState((prev) => ({
          ...prev,
          transcript: currentTranscript,
          confidence,
          currentWordIndex: index,
        }));
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error("Speech recognition error:", event.error);
        
        // Don't treat these as fatal errors
        if (event.error === "no-speech" || event.error === "aborted") {
          return;
        }

        setState((prev) => ({ 
          ...prev, 
          error: event.error,
          isListening: false 
        }));
      };

      recognition.onend = () => {
        // Auto-restart if we're still supposed to be listening
        if (isListeningRef.current) {
          try {
            recognition.start();
          } catch (e) {
            console.error("Failed to restart recognition:", e);
          }
        } else {
          setState((prev) => ({ ...prev, isListening: false }));
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error("Failed to start speech recognition:", error);
      setState((prev) => ({ 
        ...prev, 
        error: "Failed to start speech recognition",
        isListening: false 
      }));
    }
  }, [state.isSupported, calculateScrollPosition, onScrollTo]);

  const stopListening = useCallback(() => {
    isListeningRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setState((prev) => ({ ...prev, isListening: false }));
  }, []);

  const toggleListening = useCallback(() => {
    if (state.isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [state.isListening, startListening, stopListening]);

  const resetPosition = useCallback(() => {
    currentWordIndexRef.current = 0;
    setState((prev) => ({
      ...prev,
      currentWordIndex: 0,
      transcript: "",
      confidence: 0,
    }));
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        isListeningRef.current = false;
        recognitionRef.current.stop();
      }
    };
  }, []);

  return {
    ...state,
    startListening,
    stopListening,
    toggleListening,
    resetPosition,
  };
}
